import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { Row } from "@tanstack/react-table";
import { IconArrowLeft } from "@tabler/icons-react";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { Button } from "../../../../components/common/button/button";
import { PaginationAppCard } from "../../../pagination/presentation";
import { AppCard } from "../../../../components/common/card";
import { Heading, Text } from "../../../../components/common/typography";
import { Dialog } from "../../../../components/common/dialog";
import { PageContentSkeleton } from "../../../../components/common/page-content-skeleton";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useMePartner } from "../../../../swr";
import { useMyChangeRequests } from "../../hooks/useMyChangeRequests";
import { useChangeRequestDetails } from "../../hooks/useChangeRequestDetails";
import { useCancelChangeRequest } from "../../hooks/useChangeRequestMutations";
import { changeRequestsColumns } from "./change-requests-table-columns";
import { ChangeRequestsFilterContent } from "./change-requests-filter-content";
import { ChangeRequestStatusBadge } from "./change-request-status-badge";
import { ChangeRequestDetailView } from "./change-request-detail-view";
import { DraftChangeRequestActions } from "./draft-change-request-actions";
import { humanizeTypeForOwner } from "../../data/change-request-type";
import { isCancellable } from "../../data/change-request-status";
import type { ChangeRequestDto, DocumentsQueryDto } from "../../../../api";
import { DocumentsQueryDtoStatusEnum } from "../../../../api";

type StatusTab = "all" | "PENDING" | "APPROVED" | "REJECTED";

// Owned by the account page (which tab is open), not by the list's filters.
const ACCOUNT_PAGE_PARAMS = ["tab"] as const;

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: "all", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

/**
 * Embeddable change-requests section. Renders the list by default; when an
 * `id` query param is present, renders the detail view inline so buyers stay
 * scoped under the /account tab instead of bouncing to a top-level route.
 */
export function ChangeRequestsSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("id");

  if (selectedId) {
    return (
      <ChangeRequestDetailInline
        id={selectedId}
        onBack={() => {
          const next = new URLSearchParams(searchParams);
          next.delete("id");
          setSearchParams(next, { replace: false });
        }}
      />
    );
  }

  return (
    <ChangeRequestsList
      onSelect={(id) => {
        const next = new URLSearchParams(searchParams);
        next.set("id", id);
        setSearchParams(next, { replace: false });
      }}
    />
  );
}

function ChangeRequestsList({ onSelect }: { onSelect: (id: string) => void }) {
  const navigate = useNavigate();
  const { isBuyer } = useMePartner();
  const [statusTab, setStatusTab] = useState<StatusTab>("all");

  const useDataHook = useCallback(
    (query?: DocumentsQueryDto) => {
      const merged: DocumentsQueryDto = {
        ...query,
        status:
          statusTab === "all"
            ? undefined
            : (statusTab as DocumentsQueryDtoStatusEnum),
      };
      return useMyChangeRequests(merged);
    },
    [statusTab],
  );

  const handleRowClick = (row: Row<ChangeRequestDto>) => {
    onSelect(row.original.id);
  };

  const headerAction = isBuyer ? (
    <Button variant="primary" onClick={() => navigate(AppRoutes.CHANGE_REQUEST_NEW_BUYER_PROFILE)}>
      Edit profile
    </Button>
  ) : undefined;

  return (
    <div className="flex w-full flex-col gap-6">
      <SegmentedControl<StatusTab>
        options={STATUS_TABS}
        value={statusTab}
        onChange={setStatusTab}
        aria-label="Filter change requests by status"
      />

      <PaginationAppCard<ChangeRequestDto, DocumentsQueryDto>
        title="Change requests"
        color="bg-bgDefault"
        className="w-full min-h-[600px]"
        enableSearch
        searchPlaceholder="Search by request ID or note"
        filterComponent={ChangeRequestsFilterContent}
        filterTitle="Filter change requests"
        preservedUrlParams={ACCOUNT_PAGE_PARAMS}
        headerAction={headerAction}
        useDataHook={useDataHook}
        columns={changeRequestsColumns}
        onRowClick={handleRowClick}
        emptyState={{
          title:
            statusTab === "all"
              ? "No change requests yet"
              : `No ${STATUS_TABS.find((t) => t.value === statusTab)?.label.toLowerCase()} change requests`,
          description:
            statusTab === "all"
              ? "Submit a profile or product edit to see it here once it goes into review."
              : "Switch to the All tab to see your full history.",
        }}
      />
    </div>
  );
}

function ChangeRequestDetailInline({
  id,
  onBack,
}: {
  id: string;
  onBack: () => void;
}) {
  const { data: request, isLoading, error } = useChangeRequestDetails(id);
  const [cancelOpen, setCancelOpen] = useState(false);
  const { submitHandler: cancelHandler, isLoading: isCancelling } =
    useCancelChangeRequest(id);

  if (isLoading) {
    return <PageContentSkeleton cardCount={2} actionCount={1} />;
  }

  if (error || !request) {
    return (
      <AppCard title="Error" color="bg-bgDefault" className="w-full">
        <Text variant="body" className="text-accentError">
          Failed to load change request. Please try again later.
        </Text>
        <div className="mt-3">
          <Button variant="ghost" leftIcon={<IconArrowLeft size={16} />} onClick={onBack}>
            Back to change requests
          </Button>
        </div>
      </AppCard>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<IconArrowLeft size={16} />}
          onClick={onBack}
        >
          Back to change requests
        </Button>
      </div>

      <div className="flex items-center gap-4 w-full min-w-0 max-md:flex-wrap max-md:gap-3">
        <div className="flex-1 flex items-center gap-4 min-w-0 max-md:basis-full max-md:flex-wrap max-md:gap-2">
          <Heading variant="h2" tone="dark" className="truncate max-md:whitespace-normal">
            {humanizeTypeForOwner(request.type)}
          </Heading>
          <ChangeRequestStatusBadge status={request.status} />
        </div>
        <DraftChangeRequestActions request={request} disabled={isCancelling} />
        {isCancellable(request.status) && (
          <Button
            variant="secondary"
            disabled={isCancelling}
            onClick={() => setCancelOpen(true)}
          >
            Cancel request
          </Button>
        )}
      </div>

      <Dialog
        open={cancelOpen}
        setOpen={setCancelOpen}
        title="Cancel this change request?"
        text="The request will be marked as cancelled and won't be reviewed. You can create a new one later."
        buttonText={isCancelling ? "Cancelling..." : "Yes, cancel it"}
        cancelText="Keep it"
        busy={isCancelling}
        onConfirm={async () => {
          await cancelHandler();
          setCancelOpen(false);
        }}
      />

      <ChangeRequestDetailView request={request} />
    </div>
  );
}

export default ChangeRequestsSection;
